import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';
import { logError, logDebug } from '@/lib/log';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { doc, setDoc, getDoc, deleteField } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { recordNotificationOpenEvent, recordNotificationPrefChangeEvent } from '@shared/services/analyticsService';
import { stashNotificationClick, flushNotificationClicks } from '@shared/services/notificationClickService';
import { ensureDefaultInsightAnchor } from '@shared/services/insightNotificationsService';
import { useAuth } from './AuthContext';
import type { NotificationPreferences, NotificationCategory } from '@shared/types';
import { DEFAULT_NOTIFICATION_PREFERENCES, resolveCategoryPreference } from '@shared/types';

/** Preference updates may set a subset of categories — the writer merges. */
type NotificationPreferencesUpdate = Partial<Omit<NotificationPreferences, 'categories'>> & {
  categories?: Partial<Record<NotificationCategory, boolean>>;
};

interface NotificationContextType {
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown';
  preferences: NotificationPreferences;
  updatePreferences: (prefs: NotificationPreferencesUpdate) => Promise<void>;
  requestPermission: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType>({
  permissionStatus: 'unknown',
  preferences: DEFAULT_NOTIFICATION_PREFERENCES,
  updatePreferences: async () => {},
  requestPermission: async () => false,
});

export const useNotifications = () => useContext(NotificationContext);

/** Hash a token string for use as a Firestore field key */
function hashToken(token: string): string {
  let hash = 0;
  for (let i = 0; i < token.length; i++) {
    const char = token.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return 'tok_' + Math.abs(hash).toString(36);
}

function getPlatform(): 'ios' | 'android' | 'web' {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const listenersRegistered = useRef(false);
  const pendingToken = useRef<string | null>(null);
  // This device's FCM token for the current session, so we can disassociate it
  // from a user on logout (delete exactly this device's entry, not others').
  const currentTokenRef = useRef<string | null>(null);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  // Save FCM token to Firestore under userContent/{uid}
  const saveToken = useCallback(async (token: string) => {
    logDebug('[Notifications] saveToken called, hasUser:', !!currentUser);
    if (!currentUser) {
      logDebug('[Notifications] No currentUser, storing as pending');
      pendingToken.current = token;
      return;
    }
    pendingToken.current = null;
    currentTokenRef.current = token;
    try {
      const userDocRef = doc(db, 'userContent', currentUser.uid);
      const key = hashToken(token);
      const now = new Date().toISOString();
      logDebug('[Notifications] Writing token to Firestore, key:', key);
      // Stamp the device's IANA timezone alongside the token so server-side
      // schedulers can evaluate quiet hours in the user's real local time
      // (they fall back to US Eastern without it). A targeted nested merge —
      // not updatePreferences — because token saves can run before preferences
      // load, and writing the full defaults-merged object here could clobber
      // the user's stored categories/quiet hours.
      let timezone: string | undefined;
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      } catch {
        // Timezone unavailable on this webview — skip silently; servers fall back.
      }
      await setDoc(userDocRef, {
        fcmTokens: {
          [key]: {
            token,
            platform: getPlatform(),
            updatedAt: now,
            createdAt: now,
          },
        },
        ...(timezone ? { notificationPreferences: { timezone } } : {}),
      }, { merge: true });
      logDebug('[Notifications] Token saved successfully');
    } catch (err) {
      logError(err, { context: 'notifications_save_token' });
    }
  }, [currentUser]);

  // Remove THIS device's token entry from a user's doc (e.g. on logout) so a
  // push for that account can't land on whoever uses the device next. We delete
  // only this device's key — other devices the user has stay reachable. The OS
  // permission and the user's notificationPreferences are untouched, so logging
  // back in silently re-registers via the auto-register effect (no re-opt-in).
  // We do NOT call FirebaseMessaging.deleteToken(): rotating the device token is
  // unnecessary and would make re-login costlier.
  const removeTokenFromUser = useCallback(async (uid: string) => {
    const token = currentTokenRef.current;
    if (!token) return; // nothing registered this session — nothing to remove
    try {
      await setDoc(
        doc(db, 'userContent', uid),
        { fcmTokens: { [hashToken(token)]: deleteField() } },
        { merge: true }
      );
      logDebug('[Notifications] Removed device token from prior user', uid);
    } catch (err) {
      logError(err, { context: 'notifications_logout_token_cleanup' });
    }
  }, []);

  // Flush any pending token when currentUser becomes available
  useEffect(() => {
    if (currentUser && pendingToken.current) {
      saveToken(pendingToken.current);
    }
  }, [currentUser, saveToken]);

  // Load notification preferences from Firestore
  const loadPreferences = useCallback(async () => {
    if (!currentUser) return;
    try {
      const userDocRef = doc(db, 'userContent', currentUser.uid);
      const snap = await getDoc(userDocRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.notificationPreferences) {
          // Resolve each consolidated key against the stored map, which may
          // still hold legacy keys (engagement/milestone/reminder/content/
          // practice) — a legacy opt-out carries over to its new category.
          const stored = data.notificationPreferences.categories || {};
          const categories = Object.fromEntries(
            (Object.keys(DEFAULT_NOTIFICATION_PREFERENCES.categories) as NotificationCategory[])
              .map((key) => [key, resolveCategoryPreference(stored, key)]),
          ) as Record<NotificationCategory, boolean>;
          setPreferences({
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...data.notificationPreferences,
            categories,
          });
        }
      }
    } catch (err) {
      logError(err, { context: 'notifications_load_prefs' });
    }
  }, [currentUser]);

  // Update preferences in Firestore
  const updatePreferences = useCallback(async (updates: NotificationPreferencesUpdate) => {
    if (!currentUser) return;
    const merged = {
      ...preferences,
      ...updates,
      categories: {
        ...preferences.categories,
        ...(updates.categories || {}),
      },
    };
    const before = preferences;
    setPreferences(merged);
    try {
      const userDocRef = doc(db, 'userContent', currentUser.uid);
      await setDoc(userDocRef, {
        notificationPreferences: merged,
      }, { merge: true });
      // The write above overwrites in place, so this event log is the only record
      // that a change happened. Joined against the send ledger at read time to ask
      // whether a notification preceded an opt-out.
      void recordNotificationPrefChangeEvent(currentUser.uid, {
        before: before as unknown as Record<string, unknown>,
        after: merged as unknown as Record<string, unknown>,
        disabled: before.enabled !== false && merged.enabled === false,
      });
    } catch (err) {
      logError(err, { context: 'notifications_update_prefs' });
    }
  }, [currentUser, preferences]);

  // Get FCM token and save to Firestore
  const fetchAndSaveToken = useCallback(async () => {
    logDebug('[Notifications] fetchAndSaveToken called');
    try {
      const { token } = await FirebaseMessaging.getToken();
      logDebug('[Notifications] FCM token received');
      await saveToken(token);
      // Single grant chokepoint for every opt-in path: ensure a push-enabled user has
      // a default weekly insight anchor. No-op if one is already set, so it never
      // overwrites an explicit "Notify me" anchor. Awaited so an explicit opt-in that
      // calls this then writes its precise anchor wins the race.
      const uid = currentUserRef.current?.uid;
      if (uid) await ensureDefaultInsightAnchor(uid);
    } catch (err) {
      logError(err, { context: 'notifications_get_token' });
    }
  }, [saveToken]);

  // Request permission and register for push
  const requestPermission = useCallback(async (): Promise<boolean> => {
    logDebug('[Notifications] requestPermission called, platform:', Capacitor.getPlatform(), 'isNative:', Capacitor.isNativePlatform());
    if (!Capacitor.isNativePlatform()) {
      logDebug('[Notifications] Not native platform, skipping');
      setPermissionStatus('denied');
      return false;
    }

    try {
      const check = await FirebaseMessaging.checkPermissions();
      logDebug('[Notifications] Current permission status:', check.receive);
      if (check.receive === 'granted') {
        setPermissionStatus('granted');
        await fetchAndSaveToken();
        return true;
      }

      logDebug('[Notifications] Requesting permissions...');
      const result = await FirebaseMessaging.requestPermissions();
      logDebug('[Notifications] Permission result:', result.receive);
      if (result.receive === 'granted') {
        setPermissionStatus('granted');
        await fetchAndSaveToken();
        return true;
      } else {
        setPermissionStatus('denied');
        return false;
      }
    } catch (err) {
      logError(err, { context: 'notifications_permission_request' });
      setPermissionStatus('denied');
      return false;
    }
  }, [fetchAndSaveToken]);

  // Set up native push notification listeners
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || listenersRegistered.current) return;
    listenersRegistered.current = true;

    // Token refreshed — save new token to Firestore
    FirebaseMessaging.addListener('tokenReceived', (event) => {
      logDebug('[Notifications] Token refreshed');
      saveToken(event.token);
    });

    // Notification received while app is in foreground
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      logDebug('[Notifications] Foreground notification received');
    });

    // User tapped on a notification
    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const data = event.notification.data;

      // Stash the click token FIRST — a tap that cold-starts the app runs this
      // before auth resolves, which is why the uid-gated open event below misses
      // those. The stash survives the gap and flushes once a uid exists.
      stashNotificationClick(data?.nt as string | undefined);
      void flushNotificationClicks();

      // Record analytics event
      const uid = currentUserRef.current?.uid;
      if (uid) {
        recordNotificationOpenEvent(uid, {
          templateId: data?.templateId,
          title: event.notification.title,
          screen: data?.screen,
        });
      }

      if (data?.screen) {
        window.dispatchEvent(new CustomEvent('notification-navigate', { detail: data }));
      }
    });

    return () => {
      FirebaseMessaging.removeAllListeners();
      listenersRegistered.current = false;
    };
  }, [saveToken]);

  // Check current permission status and auto-register on login
  useEffect(() => {
    if (!currentUser) {
      setPermissionStatus('unknown');
      return;
    }

    loadPreferences();

    // A uid now exists — send any click token stashed by a tap that arrived before
    // auth resolved (cold start), or captured from an `?nt=` deep link on entry.
    void flushNotificationClicks();

    if (!Capacitor.isNativePlatform()) {
      setPermissionStatus('denied');
      return;
    }

    FirebaseMessaging.checkPermissions().then((result) => {
      logDebug('[Notifications] Auto-register check:', result.receive);
      if (result.receive === 'granted') {
        setPermissionStatus('granted');
        fetchAndSaveToken();
      } else if (result.receive === 'denied') {
        setPermissionStatus('denied');
      } else {
        setPermissionStatus('prompt');
      }
    });
  }, [currentUser, loadPreferences, fetchAndSaveToken]);

  // Disassociate this device's token from the previous user whenever the active
  // account changes (logout — which re-signs-in anonymously — or an account
  // switch). Centralizing it here covers every logout entry point without
  // threading a hook through each caller, and prevents cross-user misdelivery:
  // the next account's auto-register re-adds the token to its own doc.
  const prevUidRef = useRef<string | null>(null);
  useEffect(() => {
    const newUid = currentUser?.uid ?? null;
    const oldUid = prevUidRef.current;
    prevUidRef.current = newUid;
    if (oldUid && oldUid !== newUid) {
      removeTokenFromUser(oldUid);
    }
  }, [currentUser, removeTokenFromUser]);

  return (
    <NotificationContext.Provider value={{ permissionStatus, preferences, updatePreferences, requestPermission }}>
      {children}
    </NotificationContext.Provider>
  );
};
